import * as React from 'react';

import cx from 'clsx';
import styles from './Snap.module.scss';
import { createCarouselOuterStyles } from './utils';
import { useAutoplay } from '../Autoplay';

interface CarouselSnapProps {
  children: React.ReactNode[];
  visiblePartLeft?: number;
  visiblePartRight?: number;
  edgeSlidesPadding?: number;
  stopOnEach?: boolean;
  rewind?: boolean;
  lastItemPercentageWidth?: number;
  specifiedSlidesAmount?: number;
  autoplay?: number;
}

const CarouselSnap: React.FC<CarouselSnapProps> = ({
  children,
  edgeSlidesPadding = 7,
  rewind = true,
  stopOnEach = true,
  lastItemPercentageWidth,
  specifiedSlidesAmount,
  visiblePartLeft = 30,
  visiblePartRight = 30,
  autoplay,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const snapItemsRef = React.useRef<HTMLDivElement[] | null[]>([]);
  const [activeSlideIdx, setActiveSlideIdx] = React.useState<number>(0);
  const [isScrolling, setIsScrolling] = React.useState(0);

  // isScrolling is a flag being set in manual scroll so it can't be in dependency array
  React.useEffect(() => {
    if (autoplay) {
      const container = containerRef.current;
      if (container && isScrolling === 0) {
        const scrollLeft = activeSlideIdx
          ? container.offsetWidth * activeSlideIdx + 1
          : 0;
        container.scroll({ top: 0, left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [autoplay, activeSlideIdx, isScrolling]);

  const renderedSlidesAmount = specifiedSlidesAmount || children.length;
  if (renderedSlidesAmount === 1) {
    visiblePartLeft = 0;
    visiblePartRight = 0;
  }

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    const container = containerRef.current;
    if (container) {
      stopAutoplay();
      setIsScrolling(1);

      scrollTimeoutRef.current = setTimeout(() => {
        const currentElement = Math.floor(
          container.scrollLeft /
            (container.offsetWidth - visiblePartLeft - visiblePartRight)
        );
        setActiveSlideIdx(currentElement);
        startAutoplay();
        setIsScrolling(0);
      }, 1000);
    }
  };

  const moveToNextSlide = () => {
    const visibleSlides = Array.from(snapItemsRef.current).filter(
      el => el && el.getAttribute('data-hidden') === 'false'
    );
    setActiveSlideIdx(activeSlideIdx =>
      activeSlideIdx >= visibleSlides.length - 1 ? 0 : activeSlideIdx + 1
    );
  };

  const [startAutoplay, stopAutoplay] = useAutoplay(autoplay, moveToNextSlide);

  return (
    <div className={styles.containerOuter}>
      <div
        className={cx(
          styles.draggable,
          styles.container,
          styles[`carouselContainer${edgeSlidesPadding}`]
        )}
        data-testid="carousel-container"
        ref={containerRef}
        {...(autoplay ? { onTouchEnd: handleTouchEnd } : {})}
      >
        {children.map((child: any, index: number) => {
          const hidden = !!child.props?.hidden;
          return (
            <div
              className={cx(styles.outerContainer, {
                [styles.rewind]: rewind,
                [styles.stopOnEach]: stopOnEach,
                [styles.hidden]: hidden,
              })}
              style={createCarouselOuterStyles({
                slideNumber: index,
                slidesAmount: renderedSlidesAmount > 1 ? children.length : 1,
                edgeSlidesPadding,
                visiblePartLeft,
                visiblePartRight,
                lastItemPercentageWidth,
                hidden,
              })}
              data-hidden={hidden}
              data-testid="carousel-element"
              key={index}
              ref={el => (snapItemsRef.current[index] = el)}
            >
              {child}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CarouselSnap;
